import type { RecordFileItem } from '@ofmos/shared/files';
import type { FieldValue } from '../../data/client';
import { fileItemsOf } from '../../fields/files/fileItems';

/**
 * A FILES DIFF, AS A SENTENCE (`docs/decisions/file-attachments.md` D14, section 12).
 *
 * INV-4 needs no new event action for an attachment: a FILES cell changes through
 * `applyUpdate`, which emits an ordinary `updated` event with a real before/after diff, so
 * `RECORD_EVENT_ACTIONS` stays at twenty-three and the two emitters stay two. What that
 * leaves is a DISPLAY problem, and it is the only display work D14 creates: the event's
 * values are two jsonb arrays, and a raw array diff in an activity feed is unreadable.
 *
 * THE SENTENCE NAMES THE FILE WHEN THERE IS ONE TO NAME AND ONLY ONE THING HAPPENED.
 * "Added Contract-2026.pdf" is what somebody scanning a timeline is looking for; "Added 1
 * file" makes them open the event to find out which. When BOTH directions happened, section
 * 12's own example drops to counts on both sides - "Added two files and removed one" - and
 * that is the right call rather than a shortcut: two filenames in one clause is a sentence
 * nobody finishes reading, and the event is one click away.
 *
 * The numbers are SPELLED. A timeline is prose, and "Added 2 files" reads like a log line.
 */

const WORDS = [
  'no',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
] as const;

function spelled(count: number): string {
  return WORDS[count] ?? String(count);
}

/** One thing happened, so the file gets its name; more than one, and it gets a count. */
function namedOrCounted(items: readonly RecordFileItem[]): string {
  const [only] = items;
  if (items.length === 1 && only !== undefined) return only.label;
  return `${spelled(items.length)} files`;
}

/**
 * `null` when nothing moved, which is what lets the caller fall back to its ordinary
 * before/after rendering rather than printing a sentence about no change. INV-4's zero-change
 * rule means the event should not exist at all in that case; answering null is cheaper than
 * asserting it.
 */
export function fileDiffSentence(before: FieldValue, after: FieldValue): string | null {
  const beforeItems = fileItemsOf(before);
  const afterItems = fileItemsOf(after);
  const beforeIds = new Set(beforeItems.map((item) => item.fileId));
  const afterIds = new Set(afterItems.map((item) => item.fileId));

  const added = afterItems.filter((item) => !beforeIds.has(item.fileId));
  const removed = beforeItems.filter((item) => !afterIds.has(item.fileId));

  if (added.length === 0 && removed.length === 0) return null;
  if (removed.length === 0) return `Added ${namedOrCounted(added)}`;
  if (added.length === 0) return `Removed ${namedOrCounted(removed)}`;
  /**
   * The second count is BARE - "and removed one", not "and removed one file" - because the
   * noun is already in the clause before it. Section 12's example is exactly this shape.
   */
  return `Added ${spelled(added.length)} ${added.length === 1 ? 'file' : 'files'} and removed ${spelled(removed.length)}`;
}
